/*
  Featured with image.

  Extends Dawn's SliderComponent rather than reimplementing it: paged stepping,
  disabled arrow states and the ResizeObserver that recalculates on layout
  change all come for free, and this section needs exactly those. What Dawn
  does not have is a progress bar - its slider ships a numeric counter or dots,
  both of which live in SlideshowComponent, not SliderComponent - so that is
  the only thing added here.

  Two constraints from the parent class shape this file:

  1. SliderComponent's constructor returns early unless it finds a
     button[name="next"], so the section always renders arrows and hides them
     with CSS. Nothing here can assume they are visible.

  2. The parent's constructor calls initPages(), which calls update(). That
     happens before this subclass's constructor body runs, so update() is
     entered with none of this class's own fields assigned yet. Everything the
     override touches is therefore resolved lazily.
*/
class FeaturedWithImage extends SliderComponent {
  constructor() {
    super();
    this.updateProgress();
    this.updateArrows();
  }

  update() {
    super.update();
    this.updateProgress();
    this.updateArrows();
  }

  get progressBar() {
    if (this.cachedProgressBar === undefined) {
      this.cachedProgressBar = this.querySelector('.featured-with-image__progress-bar');
    }
    return this.cachedProgressBar;
  }

  updateProgress() {
    if (!this.slider) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.slider;
    const scrollable = scrollWidth - clientWidth;

    /*
      Above 990px the track is a grid and never overflows, and below it a short
      product list can fit outright. Either way there is nothing to scroll, so
      the whole control row is taken out rather than left showing a permanently
      full bar and two dead arrows.
    */
    this.classList.toggle('featured-with-image__slider--static', scrollable <= 1);

    if (!this.progressBar) return;

    /*
      Progress is measured by the trailing edge of the viewport, not the
      leading edge, so the bar is already partly filled at rest and reaches
      full exactly when the last slide is flush. A leading-edge measure would
      sit at zero on load and never reach one, which reads as broken.
    */
    const fraction = scrollable <= 1 ? 1 : (scrollLeft + clientWidth) / scrollWidth;
    this.style.setProperty('--fwi-progress', Math.min(1, Math.max(0, fraction)).toFixed(4));
  }

  /*
    Dawn decides the arrow disabled states by asking whether the first or last
    slide is fully visible, measured from integer offsetLeft and clientWidth.
    The slides here are sized as a percentage of the track so that a slide and
    a half shows at 375px, and that leaves a sub-pixel remainder on every one
    of them. The accumulated rounding is enough that the last slide never
    quite measures as "fully visible", so the next arrow stays enabled at the
    end of the track and clicking it does nothing.

    Scroll position answers the same question exactly, so it is used to
    correct both buttons after the parent has set them.
  */
  updateArrows() {
    if (!this.slider || !this.prevButton || !this.nextButton) return;
    const scrollable = this.slider.scrollWidth - this.slider.clientWidth;
    this.prevButton.toggleAttribute('disabled', this.slider.scrollLeft <= 1);
    this.nextButton.toggleAttribute('disabled', this.slider.scrollLeft >= scrollable - 1);
  }
}

customElements.define('featured-with-image', FeaturedWithImage);
